import React, { useEffect, useState } from 'react';
import RequestFormLayout from 'components/layout/RequestFormLayout';
import { notification } from 'antd';
import { useFormCleanup } from 'core/utils/useFormCleanup';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, CombinedStates } from 'core/types';
import useAccessFlow from 'core/accessRequestForm/useAccessFlow';
import { deleteAccessRequest, uploadAccessRequestAttachment } from 'core/accessRequestForm/accessRequestFormApi';
import { VisitRequestFormResponsePayload, SiteAreaAccessType, SiteAreaAccessTypeQuestion, AccessTypeAnswer } from 'core/accessRequestForm/types';
import { unwrapResult } from '@reduxjs/toolkit';
import AccessTypeSelector from 'components/Form/AccessTypeSelector';
import FormSection from 'components/Form/FormSection';
import FileUploader from 'components/Form/FileUploader';
import UploadedFileList from 'components/Form/UploadedFilesList';
import type { UploadFile } from 'antd/lib/upload/interface';

import { ACCESS_STEPPER_LABELS, ACCESS_STEPPER_LABELS_RETAIL } from 'core/utils/stepperLabels';
import { reportUnexpectedRejection } from 'core/utils/reportUnexpectedRejection';

const isEmptyAnswer = (q: SiteAreaAccessTypeQuestion, value?: string): boolean => {
    if (!value) return true;
    if (q.type === 'select' && q.allowMultiple) {
        try {
            return JSON.parse(value).length === 0;
        } catch {
            return true;
        }
    }
    return value.trim() === '';
};

const AccessSiteAccessForm: React.FC = () => {
    const [selectedAccessTypeId, setSelectedAccessTypeId] = useState<number | null>(null);
    const [answers, setAnswers] = useState<Record<number, string>>({});

    const { stepData, visitRequest } = useSelector((state: CombinedStates) => state.accessRequestForm);
    const isRetail = stepData?.isRetail || false;
    const { id } = useParams<{ id: string }>();
    const flow = useAccessFlow();

    const { state: navState } = useLocation();
    const amending = navState?.amending;

    const navigation = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    // Auto-cleanup request if user leaves without completing
    useFormCleanup({
        requestId: (!flow.guestMode && !amending) ? visitRequest.encryptedId : null,
        formBasePath: '/access-request/',
        deleteEndpoint: 'access-request',
        dispatchDelete: () => {
            if (visitRequest.encryptedId) dispatch(deleteAccessRequest(visitRequest.encryptedId));
        },
    });

    const handleBackClick = (navigationState: Record<string, unknown>) => {
        flow.goToStep(3, navigationState);
    };

    const handleInitialLoad = async () => {
        if (id !== undefined || flow.guestMode) {
            const resultAction = await flow.loadStep(4, amending);

            // Redirect to dashboard if access request not found
            if (resultAction.meta.requestStatus === 'rejected') {
                // A guest has no dashboard and no way to create a request: send
                // them back to the link itself, which re-validates the token and
                // shows the invalid/expired message when that is the reason.
                if (flow.guestMode) {
                    navigation(`/booking/${flow.token}`, { replace: true });
                    return;
                }

                navigation('/dashboard', { replace: true });
                notification.error({
                    message: 'Access Request Not Found',
                    description: 'The access request could not be found. Please create a new request.',
                });
            }
        } else {
            navigation('/dashboard', { replace: true });
            notification.error({
                message: 'Invalid Access Request',
                description: 'Please select an access request to proceed',
            });
        }
    };

    useEffect(() => {
        handleInitialLoad();
    }, []);

    // The Access Type step is not part of the retail flow. If a retail request
    // reaches this page (e.g. via a stale/bookmarked URL), send it to Job Details
    // — the step that immediately follows Add Visitor in the retail chain.
    useEffect(() => {
        if ((id !== undefined || flow.guestMode) && stepData?.isRetail) {
            flow.goToStep(1);
        }
    }, [stepData?.isRetail, id, flow.guestMode]);

    // Pre-fill selections from stepData (when loading step 4 directly)
    useEffect(() => {
        if (stepData.selectedAccessTypeId !== undefined) {
            setSelectedAccessTypeId(stepData.selectedAccessTypeId ?? null);
        }

        if (stepData.existingAnswers !== undefined) {
            if (stepData.existingAnswers?.length) {
                const existingAnswerMap: Record<number, string> = {};
                stepData.existingAnswers.forEach((a) => {
                    existingAnswerMap[a.questionId] = a.answer;
                });
                setAnswers(existingAnswerMap);
            } else {
                setAnswers({});
            }
        }
    }, [stepData.selectedAccessTypeId, stepData.existingAnswers]);

    // Pre-fill from visitRequest data (fallback for back navigation before stepData loads).
    // Skip if stepData has already provided values to avoid overwriting authoritative data.
    useEffect(() => {
        if (stepData.selectedAccessTypeId !== undefined || stepData.existingAnswers !== undefined) return;

        if (visitRequest.accessTypeId) {
            setSelectedAccessTypeId(visitRequest.accessTypeId);
        }
        if (visitRequest.accessTypeAnswers?.length) {
            const existingAnswerMap: Record<number, string> = {};
            visitRequest.accessTypeAnswers.forEach((a) => {
                existingAnswerMap[a.questionId] = a.answer;
            });
            setAnswers(existingAnswerMap);
        } else {
            setAnswers({});
        }
    }, [visitRequest.accessTypeId, visitRequest.accessTypeAnswers, stepData.selectedAccessTypeId, stepData.existingAnswers]);

    const accessTypes: SiteAreaAccessType[] = stepData.accessTypes || [];
    const selectedAccessType = accessTypes.find((at) => at.id === selectedAccessTypeId);
    const questions = selectedAccessType?.questions || [];

    const handleAccessTypeChange = (accessTypeId: number | null) => {
        setSelectedAccessTypeId(accessTypeId);
        // Clear answers when access type changes
        setAnswers({});
    };

    const handleAnswerChange = (questionId: number, value: string) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
    };

    const handleFileUploadChange = (files: UploadFile[]) => {
        const lastFile = files[files.length - 1];
        if (lastFile && id) {
            dispatch(uploadAccessRequestAttachment({
                id: String(id),
                attachment: lastFile,
                step: 'access',
            }));
        }
    };

    const handleNextClick = (navigationState: Record<string, unknown>) => {
        if (accessTypes.length > 0 && !selectedAccessTypeId) {
            notification.error({
                message: 'Access Type Required',
                description: 'Please select an access type to proceed.',
            });
            return;
        }

        // Validate required questions
        const missingRequired = questions.filter(
            (q) => q.isRequired && isEmptyAnswer(q, answers[q.id]),
        );

        if (missingRequired.length > 0) {
            notification.error({
                message: 'Required Fields',
                description: `Please fill in all required fields: ${missingRequired.map((q) => q.label).join(', ')}`,
            });
            return;
        }

        // Build answers array for submission
        const answersPayload: AccessTypeAnswer[] = questions
            .filter((q) => !isEmptyAnswer(q, answers[q.id]))
            .map((q) => ({ questionId: q.id, answer: answers[q.id] as string }));

        if (id !== undefined || flow.guestMode) {
            flow.updateStep(4, {
                accessTypeId: selectedAccessTypeId,
                answers: answersPayload,
                amending,
                forcedStep: 4,
            }).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    flow.goToStep(isRetail ? 1 : 5, navigationState);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    return (
        <RequestFormLayout
            currentStep={isRetail ? 3 : 4}
            isNextEnabled
            isBackEnabled
            onBackClick={handleBackClick}
            onNextClick={handleNextClick}
            stepperLabels={isRetail ? ACCESS_STEPPER_LABELS_RETAIL : ACCESS_STEPPER_LABELS}
            formTitle="Access Request Form"
            reduxSliceKey="accessRequestForm">
            <>
                <AccessTypeSelector
                    accessTypes={accessTypes}
                    selectedAccessTypeId={selectedAccessTypeId}
                    onChange={handleAccessTypeChange}
                    questions={questions}
                    answers={answers}
                    onAnswerChange={handleAnswerChange} />
                <FormSection title="Supporting Documentation">
                    <FileUploader onChange={handleFileUploadChange} />
                    <UploadedFileList reduxSliceKey="accessRequestForm" attachmentKey="accessAttachments" />
                </FormSection>
            </>
        </RequestFormLayout>
    );
};

export default AccessSiteAccessForm;
